import { Box, Collapse, Text, useDisclosure } from '@chakra-ui/react';
import { ChevronDownIcon, LockIcon } from '@chakra-ui/icons';
import type { PaymentMethod, Product } from '../useCheckoutForm';
import { engagementLabel, formatAmount, productTitle, recurringLabel, sessionsLabel, validityLabel } from './format';
import { Eyebrow } from './Section';

export interface OrderSummaryProps {
  product: Product;
  isContract: boolean;
  productAmount: number;
  discount: number;
  promoCode?: string;
  giftCardValue?: number;
  totalAmount: number;
  remainingAmount: number;
  vatAmount: number;
  paymentMethod: string;
  selectedPlan: number | null;
  paymentPlans: PaymentMethod[] | null;
  /** Conteneur étroit : bandeau replié « Votre commande · 28 € » dépliable. */
  compact?: boolean;
}

function Line({ label, value, muted, negative }: { label: string; value: string; muted?: boolean; negative?: boolean }) {
  return (
    <Box className={`cv2-line${muted ? ' cv2-line--muted' : ''}${negative ? ' cv2-line--negative' : ''}`}>
      <Text className="cv2-line-label">{label}</Text>
      <Text className="cv2-line-value cv2-num">
        {negative ? '− ' : ''}
        {value}
      </Text>
    </Box>
  );
}

/**
 * Récapitulatif de commande : carte sombre, total en couleur de marque
 * (promotion du bloc `.m-total` de la maquette mobile.html au rang de carte
 * entière). Collé en haut de la colonne de droite sur ordinateur ; replié
 * au-dessus du formulaire dans un conteneur étroit.
 */
export function OrderSummary(props: OrderSummaryProps) {
  const { product, isContract, productAmount, discount, promoCode, giftCardValue, totalAmount, remainingAmount, vatAmount, paymentMethod, selectedPlan, paymentPlans, compact } = props;
  const { isOpen, onToggle } = useDisclosure();

  const meta: string[] = [];
  const brand = typeof mondayMindbody !== 'undefined' ? mondayMindbody?.customization?.brandName : undefined;
  if (brand) meta.push(brand);
  if (product.type === 'service') {
    const s = sessionsLabel(product.Count);
    const v = validityLabel(product);
    if (s) meta.push(s);
    if (v) meta.push(v);
  } else if (product.type === 'contract') {
    const e = engagementLabel(product);
    if (e) meta.push(e);
  } else {
    meta.push('Carte cadeau');
  }

  const giftCardApplied = giftCardValue ? Math.min(giftCardValue, totalAmount) : 0;
  const plan = paymentMethod === 'Alma' && selectedPlan ? paymentPlans?.find((p) => p.installmentsCount === selectedPlan) : null;
  const firstInstallment = plan?.planDetails?.[0]?.totalAmount;
  const recurring = recurringLabel(product);
  const title = isContract ? 'Votre abonnement' : 'Votre commande';

  const body = (
    <Box className="CheckoutV2SummaryBody">
      {!compact && (
        <>
          <Text className="cv2-summary-title">{productTitle(product)}</Text>
          {meta.length > 0 && <Text className="cv2-summary-meta">{meta.join(' · ')}</Text>}
        </>
      )}
      {compact && meta.length > 0 && <Text className="cv2-summary-meta">{meta.join(' · ')}</Text>}

      <Box className="cv2-summary-lines">
        <Line label={isContract ? 'Premier prélèvement' : 'Prix'} value={formatAmount(productAmount)} />
        {discount > 0 && <Line label={promoCode ? `Code ${promoCode}` : 'Remise'} value={formatAmount(discount)} negative />}
        {giftCardApplied > 0 && <Line label="Carte cadeau" value={formatAmount(giftCardApplied)} negative />}
        <Line label="dont TVA 20 %" value={formatAmount(vatAmount)} muted />
      </Box>

      <Box className="cv2-total CheckoutV2Total">
        <Text className="cv2-total-label">{isContract ? "À payer aujourd'hui" : 'À payer'}</Text>
        <Text className="cv2-total-amount cv2-num">{formatAmount(remainingAmount)}</Text>
      </Box>
      {plan && firstInstallment !== undefined && (
        <Text className="cv2-total-sub">
          {selectedPlan} × {formatAmount(Number(firstInstallment))}
        </Text>
      )}
      {isContract && recurring && <Text className="cv2-total-sub">{recurring}</Text>}

      <Box className="cv2-secure">
        <LockIcon boxSize="10px" />
        <span>Paiement sécurisé · 3D Secure</span>
      </Box>
    </Box>
  );

  if (compact) {
    return (
      <Box className="cv2-summary cv2-summary--compact CheckoutV2Summary">
        <button type="button" className="cv2-summary-toggle" onClick={onToggle} aria-expanded={isOpen}>
          <Box minW={0}>
            <Eyebrow>{title}</Eyebrow>
            <Text className="cv2-summary-title" noOfLines={1}>
              {productTitle(product)}
            </Text>
          </Box>
          <Box display="flex" alignItems="center" gap="8px" flexShrink={0}>
            <Text className="cv2-summary-toggle-amount cv2-num">{formatAmount(remainingAmount)}</Text>
            <ChevronDownIcon boxSize="20px" color="rgba(255,255,255,0.6)" transform={isOpen ? 'rotate(180deg)' : undefined} transition="transform .2s" />
          </Box>
        </button>
        <Collapse in={isOpen}>
          <Box className="cv2-summary-compact-body">{body}</Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box className="cv2-summary CheckoutV2Summary">
      <Eyebrow>{title}</Eyebrow>
      {body}
    </Box>
  );
}
